import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Pencil, Rocket } from 'lucide-react';

import {
  useActivateSystemPromptVersion,
  useCreateSystemPromptVersion,
  useSystemPromptVersions,
} from '../../../services/systemPrompt.service';
import { SystemPrompt } from '../../../types/admin';

import { PromptDiffView } from './PromptDiffView';
import { PromptVersionList } from './PromptVersionList';

interface PromptVersionsDialogProps {
  prompt: SystemPrompt | null;
  open: boolean;
  onClose: () => void;
}

const ContentPane: React.FC<{ title: string; content: string }> = ({ title, content }) => (
  <Box className="min-w-0 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
    <Typography variant="subtitle2" className="mb-2 text-gray-900 dark:text-slate-100">
      {title}
    </Typography>
    <Typography
      variant="body2"
      component="pre"
      className="max-h-[50vh] overflow-auto whitespace-pre-wrap text-slate-700 dark:text-slate-300"
    >
      {content}
    </Typography>
  </Box>
);

export const PromptVersionsDialog: React.FC<PromptVersionsDialogProps> = ({
  prompt,
  open,
  onClose,
}) => {
  const key = open && prompt ? prompt.key : null;
  const { data: versions = [], isLoading } = useSystemPromptVersions(key);
  const createMutation = useCreateSystemPromptVersion();
  const activateMutation = useActivateSystemPromptVersion();

  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [confirmActivate, setConfirmActivate] = useState(false);

  useEffect(() => {
    if (!open || !prompt) return;
    setSelectedVersion(prompt.activeVersion ?? prompt.latestVersion);
    setEditing(false);
    setConfirmActivate(false);
    setError('');
    // Reset only when the dialog opens or switches key, not on every list refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prompt?.key]);

  const selected = useMemo(
    () => versions.find((item) => item.version === selectedVersion) ?? null,
    [versions, selectedVersion]
  );
  const active = useMemo(
    () => versions.find((item) => item.version === prompt?.activeVersion) ?? null,
    [versions, prompt?.activeVersion]
  );

  if (!prompt) return null;

  const isSelectedActive = selected?.version === prompt.activeVersion;
  const busy = createMutation.isPending || activateMutation.isPending;

  const startEdit = () => {
    setDraft(selected?.content ?? prompt.content);
    setNote('');
    setError('');
    setEditing(true);
  };

  const handleCreate = async () => {
    if (!draft.trim()) {
      setError('Content is required');
      return;
    }
    const created = await createMutation.mutateAsync({
      key: prompt.key,
      content: draft,
      note: note.trim() || null,
    });
    setSelectedVersion(created.version);
    setEditing(false);
  };

  const handleActivate = async () => {
    if (selected === null) return;
    await activateMutation.mutateAsync({ key: prompt.key, version: selected.version });
    setConfirmActivate(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          <span className="font-mono">{prompt.key}</span> versions
        </DialogTitle>
        <DialogContent>
          <Box className="mt-2 grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <PromptVersionList
                versions={versions}
                activeVersion={prompt.activeVersion}
                selectedVersion={selectedVersion}
                onSelect={(version) => {
                  setSelectedVersion(version);
                  setEditing(false);
                }}
              />
            )}

            <Box className="min-w-0 space-y-4">
              {prompt.hasPendingVersion && (
                <Alert severity="warning">
                  A newer version exists but is not active. Runtime still serves v
                  {prompt.activeVersion}.
                </Alert>
              )}

              {editing ? (
                <>
                  <TextField
                    fullWidth
                    label="Prompt Content (new version)"
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      setError('');
                    }}
                    error={!!error}
                    helperText={error || 'Placeholders use {{token}} syntax.'}
                    multiline
                    minRows={14}
                  />
                  <TextField
                    fullWidth
                    label="Note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                  />
                </>
              ) : (
                <>
                  {selected && active && !isSelectedActive ? (
                    <PromptDiffView
                      beforeTitle={`Active: v${active.version}`}
                      afterTitle={`Selected: v${selected.version}`}
                      before={active.content}
                      after={selected.content}
                    />
                  ) : (
                    selected && (
                      <ContentPane
                        title={`Selected: v${selected.version}${isSelectedActive ? ' (active)' : ''}`}
                        content={selected.content}
                      />
                    )
                  )}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={busy}
                startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
              >
                Save as new version
              </Button>
            </>
          ) : (
            <>
              <Button startIcon={<Pencil size={18} />} onClick={startEdit} disabled={!selected}>
                New version from this
              </Button>
              <Box className="flex-1" />
              <Button onClick={onClose}>Close</Button>
              <Button
                variant="contained"
                startIcon={<Rocket size={18} />}
                disabled={!selected || isSelectedActive || busy}
                onClick={() => setConfirmActivate(true)}
              >
                Activate
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmActivate} onClose={() => setConfirmActivate(false)}>
        <DialogTitle>Activate v{selected?.version}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Runtime will serve this version for <span className="font-mono">{prompt.key}</span>.
            Cached copies expire after the runtime config TTL.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmActivate(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleActivate}
            disabled={activateMutation.isPending}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
